import { useState, useEffect, useRef, useCallback } from "react";
import zoomSdk from "@zoom/appssdk";
import { ROLES, BUILT_IN_AGENT, SUMMARIZE_STATES, VIEWS, SESSION_TABS } from "../../constants";
import { SCHEDULED_AGENTS_ALL_INACTIVE_MESSAGE, AGENT_TOGGLE_ERROR_MESSAGE } from "./home.constants";
import LogManager from "../../utils/logManager";
import { onSummarize, onEndSummarize, createRTMSChangeHandler } from "./home.utils";
import { truncateDisplayName, scheduledAgentsPartialErrorMessage } from "../../utils/agentDisplayUtils";
import {
  fetchAvailableExtensions,
  fetchActiveAgents,
  enableExtension,
  disableExtension,
} from "../../utils/agenticExtensionUtils";
import useAutoActivateScheduledAgents from "../../hooks/useAutoActivateScheduledAgents";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { usePublishedContent } from "../../hooks/usePublishedContent";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useInsightStore } from "../../hooks/useInsightStore";
import { useActionItemsSeed } from "../insights/actionItems/actionItems.hooks";
import { useAgendaSeed } from "../insights/agenda/agenda.hooks";
import {
  fromBackend as fromBackendAgendaItem,
  compareAgendaItems,
} from "../insights/agenda/agenda.utils";

/**
 * useAgenticExtensions - owns the agentic-assistant extension catalog and the enabled-agent set:
 * fetching available/active agents, auto-activating the organizer's schedule-time selection, the
 * organizer toggle, and the partial-failure / all-inactive toasts. Extracted from Home.jsx.
 *
 * Toasts are surfaced through the injected `emitToast(message)` so Home keeps a single toast queue
 * shared with non-agentic sources (e.g. preference-save failures).
 *
 * @returns {{ availableExtensions, enabledAgentIds, agentsLoading, applyActiveAgents,
 *             onToggleAgent, onPopoverOpen }}
 */
export const useAgenticExtensions = ({
  isAgenticEnabled,
  isTranscriptOnly,
  role,
  meetingUUID,
  meetingId,
  rtmsStreamId,
  summarizeState,
  emitToast,
}) => {
  const [availableExtensions, setAvailableExtensions] = useState([]);
  const [enabledAgentIds, setEnabledAgentIds] = useState([BUILT_IN_AGENT.id]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Scheduled agents currently active this session (auto-activated or manually toggled on) —
  // merged into enabledAgentIds so a late-resolving pre-activation /available-extensions fetch
  // can't drop them. Reset on each session boundary so a fresh snapshot is authoritative.
  const scheduledActivatedIdsRef = useRef([]);
  // Failed scheduled-agent ids awaiting a partial-failure toast: set when a partial failure is
  // reported before the /available-extensions catalog has resolved (names can't resolve yet);
  // flushed with names once that fetch resolves.
  const pendingPartialFailedIdsRef = useRef(null);
  // The /available-extensions catalog once its fetch resolves — the authoritative "loaded"
  // signal: null = not resolved yet, [] = resolved but empty, [...] = resolved with rows. Using
  // this (not availableExtensions.length) distinguishes "not loaded" from "loaded-empty", so a
  // queued partial-failure toast is never stranded when the fetch resolves empty.
  const resolvedCatalogRef = useRef(null);

  // Build + show the Case-2 partial-failure toast, resolving failed ids to (truncated) display
  // names from the supplied catalog. A name that can't be resolved is dropped; the message
  // helper falls back to a name-free "some agents are inactive" when none resolve.
  const emitPartialFailureToast = (failedIds, extensions) => {
    const failedNames = (failedIds ?? [])
      .map((id) => {
        const ext = extensions.find((e) => e.extensionId === id);
        return truncateDisplayName(ext?.displayName || ext?.defaultDisplayName);
      })
      .filter(Boolean);
    emitToast(scheduledAgentsPartialErrorMessage(failedNames));
  };

  // Apply a fresh /active-agents result. `agents` is null when the fetch failed —
  // keep the previous list in that case rather than blanking every agent.
  const applyActiveAgents = (agents) => {
    if (!agents) return;
    setAvailableExtensions(agents);
    setEnabledAgentIds([BUILT_IN_AGENT.id, ...agents.map((a) => a.extensionId)]);
  };

  // Reset the per-session tracking refs whenever the session is not active, so each new
  // SUMMARIZING episode's /available-extensions snapshot is authoritative (a stale ref must
  // not re-add an agent an external actor deactivated between sessions).
  useEffect(() => {
    if (summarizeState !== SUMMARIZE_STATES.SUMMARIZING) {
      scheduledActivatedIdsRef.current = [];
      pendingPartialFailedIdsRef.current = null;
      resolvedCatalogRef.current = null;
    }
  }, [summarizeState]);

  // Fetch available/active extensions once session is active (agentic mode only)
  useEffect(() => {
    if (!isAgenticEnabled) return;
    // Same reason as wsEnabled: transcript mode never renders the extension UI, so
    // fetching it only produces a HIRE denial and discarded state.
    if (isTranscriptOnly) return;
    if (!role || !meetingUUID) return;
    if (summarizeState !== SUMMARIZE_STATES.SUMMARIZING) return;

    let stale = false;
    if (role === ROLES.ORGANIZER) {
      fetchAvailableExtensions(meetingUUID).then((extensions) => {
        if (stale) return;
        setAvailableExtensions(extensions);
        const enabledFromApi = extensions
          .filter((ext) => ext.enabled)
          .map((ext) => ext.extensionId);
        // Union in any scheduled agents already auto-activated this session: this fetch may
        // have been issued pre-activation but resolve after it, and its snapshot would
        // otherwise report them as off and drop them.
        setEnabledAgentIds([BUILT_IN_AGENT.id, ...new Set([...enabledFromApi, ...scheduledActivatedIdsRef.current])]);
        // Mark the catalog resolved (even if empty) so a later partial failure emits immediately
        // instead of queueing forever, then flush any toast queued before this resolved — with
        // names from this fresh result (an empty result yields the name-free fallback).
        resolvedCatalogRef.current = extensions;
        if (pendingPartialFailedIdsRef.current) {
          emitPartialFailureToast(pendingPartialFailedIdsRef.current, extensions);
          pendingPartialFailedIdsRef.current = null;
        }
      });
    } else {
      fetchActiveAgents(meetingUUID).then((agents) => {
        if (stale) return;
        applyActiveAgents(agents);
      });
    }
    return () => { stale = true; };
    // isTranscriptOnly belongs here: it is derived from aimsStatus, which arrives
    // asynchronously from the availability API. Omitting it would let the effect
    // run with a stale value and fetch anyway on the render where the mode is
    // first known.
  }, [isAgenticEnabled, isTranscriptOnly, role, meetingUUID, summarizeState]);

  // Auto-activate the organizer's schedule-time agent selection once the session is active.
  // It only reacts to an already-active session.
  useAutoActivateScheduledAgents({
    isAgenticEnabled,
    role,
    meetingId,
    meetingUuid: meetingUUID,
    aimsUuid: rtmsStreamId,
    summarizeState,
    onAgentsActivated: (activatedIds) => {
      // Merge locally (like the manual toggle) instead of re-fetching, and record the ids so the
      // organizer /available-extensions effect's write can't drop them (see scheduledActivatedIdsRef).
      // Activated agents intentionally stay ACTIVE past session end so they still receive the
      // post-summary event — they are only turned off by an explicit manual toggle.
      scheduledActivatedIdsRef.current = [...new Set([...scheduledActivatedIdsRef.current, ...activatedIds])];
      setEnabledAgentIds((prev) => [...new Set([...prev, ...activatedIds])]);
    },
    onError: (info) => {
      if (info?.scope === "partial") {
        // Case 2 (partial): name the failed agents from the /available-extensions catalog.
        // If the catalog hasn't resolved yet (its fetch races Phase-2 activation on SUMMARIZING),
        // queue the ids and let the catalog effect emit the named toast once it resolves — instead
        // of degrading to a name-free message. resolvedCatalogRef (not availableExtensions.length)
        // is the load signal, so a catalog that resolved *empty* still emits now (generic) rather
        // than queueing a toast that would never flush.
        if (resolvedCatalogRef.current) {
          emitPartialFailureToast(info.failedIds, resolvedCatalogRef.current);
        } else {
          pendingPartialFailedIdsRef.current = info.failedIds ?? [];
        }
        return;
      }
      // Case 1 (fetch failed) and Case 3 (all failed) → "all inactive".
      emitToast(SCHEDULED_AGENTS_ALL_INACTIVE_MESSAGE);
    },
  });

  const onPopoverOpen = () => {
    if (role !== ROLES.ORGANIZER && meetingUUID) {
      setAgentsLoading(true);
      fetchActiveAgents(meetingUUID).then(applyActiveAgents).finally(() => setAgentsLoading(false));
    }
  };

  const onToggleAgent = async (id, checked) => {
    if (id === BUILT_IN_AGENT.id) return;
    if (checked && !rtmsStreamId) return;
    const toggleAgent = (prev, add) =>
      add ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id);
    setEnabledAgentIds((prev) => toggleAgent(prev, checked));
    const result = checked
      ? await enableExtension(meetingUUID, meetingId, id, rtmsStreamId)
      : await disableExtension(meetingUUID, id);
    if (!result.success) {
      // Revert the UI; leave scheduledActivatedIdsRef untouched — a failed disable
      // means the agent is still ACTIVE, so it must stay tracked.
      setEnabledAgentIds((prev) => toggleAgent(prev, !checked));
      emitToast(AGENT_TOGGLE_ERROR_MESSAGE);
      return;
    }
    // Keep scheduledActivatedIdsRef in sync with the backend so the
    // /available-extensions union neither drops nor resurrects this agent: remove it
    // once a disable is confirmed, re-add it once an enable is confirmed.
    if (!checked) {
      scheduledActivatedIdsRef.current = scheduledActivatedIdsRef.current.filter((x) => x !== id);
    } else if (!scheduledActivatedIdsRef.current.includes(id)) {
      scheduledActivatedIdsRef.current = [...scheduledActivatedIdsRef.current, id];
    }
  };

  return {
    availableExtensions,
    enabledAgentIds,
    agentsLoading,
    applyActiveAgents,
    onToggleAgent,
    onPopoverOpen,
  };
};

/**
 * useRealtimeStores - owns the single WebSocket connection and the real-time stores it feeds:
 * the published-content store (UX Publish) and the insight store (action items + agenda), plus
 * the on-session-start seed of both insight lists. Extracted from Home.jsx.
 *
 * The WebSocket is a single connection for all push (UX Publish + Insight Push). It's activated
 * only while summarizing, gated by agentic mode (UX Publish) or the insight-push flag (its own
 * AppConfig-driven flag so the WS backbone can ramp independently). wsConnected is driven off the
 * real socket lifecycle (open/close), not intent, so fallback polling stays active when the
 * handshake fails / retries / gives up.
 *
 * @returns {{ publishedContentStore, insightStore }}
 */
export const useRealtimeStores = ({
  meetingUUID,
  isAgenticEnabled,
  isTranscriptOnly,
  summarizeState,
}) => {
  const featureFlags = useFeatureFlags();
  const isInsightPushEnabled = featureFlags?.isInsightPushEnabled === true;
  const wsEnabled =
    (isAgenticEnabled || isInsightPushEnabled) && !isTranscriptOnly && summarizeState === SUMMARIZE_STATES.SUMMARIZING;
  const publishedContentStore = usePublishedContent(meetingUUID, wsEnabled && isAgenticEnabled);

  // Insight store: receives WebSocket push messages, maintains action items + agenda state.
  // Agenda pushes arrive in the backend shape, so hand the store the agenda mapper.
  const insightStore = useInsightStore({
    mapAgendaItem: fromBackendAgendaItem,
    sortAgendaItems: compareAgendaItems,
  });

  // Seed the action-items badge/store on session start so the badge seeds without waiting for the
  // tab to be opened.
  useActionItemsSeed({
    meetingUuid: meetingUUID,
    insightStore,
    isSessionActive: summarizeState === SUMMARIZE_STATES.SUMMARIZING,
    isTranscriptOnly,
    isEnabled: featureFlags?.isActionItemsEnabled,
  });

  // Agenda items live in insightStore (single source of truth, matching Action Items).
  // The seed/poll hook writes there; the tab header badge and Agenda component read from there.
  useAgendaSeed({
    meetingUuid: meetingUUID,
    insightStore,
    isSessionActive: summarizeState === SUMMARIZE_STATES.SUMMARIZING,
    isTranscriptOnly,
    isEnabled: featureFlags?.isAgendaEnabled,
  });

  // WebSocket message dispatcher — routes by data.type.
  const handleWsMessage = useCallback((data) => {
    // Gate ux_publish on agentic mode so dispatch stays in lockstep with the (agentic-only)
    // catch-up fetch and non-agentic sessions don't write a partial published-content store.
    if (data.type === "ux_publish" && isAgenticEnabled) {
      publishedContentStore.handlePublishMessage(data);
    } else if (data.type === "insight_update") {
      insightStore.handleInsightMessage(data);
    }
  }, [publishedContentStore, insightStore, isAgenticEnabled]);

  useWebSocket(wsEnabled ? meetingUUID : null, handleWsMessage, {
    onConnectionChange: insightStore.setWsConnected,
  });

  return { publishedContentStore, insightStore };
};

/**
 * useSummarizationController - owns the in-meeting RTMS/summarization state machine that was
 * previously inlined in Home.jsx: the summarize + rtms-error state cluster, the timer/session
 * refs, the ref-sync effects, the Zoom RTMS status-change listener, and the start()/end()
 * actions. Keeping it here makes Home a thin view and gives the machine a single testable home.
 *
 * `start({ isRestartAttempt })` and `end()` wrap the onSummarize/onEndSummarize orchestration so
 * callers (DefaultView, ErrorModals, the auto-start / end-button paths) no longer thread ~15
 * setters + refs by hand. The setters are still exposed for the init hook (useInMeetingInitialization
 * seeds several of them) and for the error-modal reset paths.
 *
 * @returns state values, the setters those two consumers still need, the session/timer refs read
 *          during render, and the start/end actions.
 */
export const useSummarizationController = ({
  meetingUUID,
  meetingId,
  role,
  alias,
  appContext,
  isLoading,
  isTranscriptOnly,
}) => {
  const [summarizeState, setSummarizeState] = useState(SUMMARIZE_STATES.DEFAULT);
  const [isRestarting, setIsRestarting] = useState(false);
  const [isRtmsStartedByOther, setIsRtmsStartedByOther] = useState(false);
  const [rtmsRequesterName, setRtmsRequesterName] = useState(null);
  const [rtmsStreamId, setRtmsStreamId] = useState(null);
  const [summarizeClickCount, setSummarizeClickCount] = useState(0);
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);
  const [isRtmsErrorModalOpen, setIsRtmsErrorModalOpen] = useState(false);
  const [rtmsErrorType, setRtmsErrorType] = useState(null);
  const [isProductFeedbackModalOpen, setIsProductFeedbackModalOpen] = useState(false);
  // TODO: aimsUuid/summaryData are set by checkForSummary once the READY-state UI lands.
  const [aimsUuid, setAimsUuid] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  // Refs mirroring state for use inside async callbacks, plus RTMS session-control timers.
  const summarizeStateRef = useRef(summarizeState);
  const isRtmsStartedByOtherRef = useRef(isRtmsStartedByOther);
  const rtmsStartTimestampRef = useRef(null);
  const hideStoppedComponentTimerRef = useRef(null);
  const sessionStatusIntervalRef = useRef(null);
  const sessionStatusTimeoutRef = useRef(null);
  const rejectionTimeoutRef = useRef(null);
  const isEndingRef = useRef(false);

  // Keep summarizeStateRef in sync; once out of LOADING clear the ending/restart flags.
  useEffect(() => {
    summarizeStateRef.current = summarizeState;
    if (summarizeState !== SUMMARIZE_STATES.LOADING) {
      isEndingRef.current = false;
      if (isRestarting) setIsRestarting(false);
    }
  }, [summarizeState]);

  useEffect(() => {
    isRtmsStartedByOtherRef.current = isRtmsStartedByOther;
  }, [isRtmsStartedByOther]);

  // Set up the RTMS status change listener.
  useEffect(() => {
    // Skip in transcript mode: TranscribeAvailable owns the RTMS status listener there.
    // Registering this summarization handler too causes a race that clobbers
    // isRtmsStartedByOther (in multiple/per-host mode the lock is bypassed, so
    // /session returns a null requester which this handler misreads as "started by other").
    if (window.location.href !== "http://localhost:3000/" && meetingUUID && !isLoading && !isTranscriptOnly) {
      try {
        LogManager.info(`Setting up RTMS status change listener for meeting: ${meetingUUID}`);
        const handleRTMSChange = createRTMSChangeHandler({
          meetingUUID,
          alias,
          setSummarizeState,
          setIsRtmsStartedByOther,
          setRtmsRequesterName,
          setRtmsStreamId,
          setRtmsErrorType,
          setIsRtmsErrorModalOpen,
          setIsProductFeedbackModalOpen,
          setAimsUuid,
          setSummaryData,
          summarizeStateRef,
          isRtmsStartedByOtherRef,
          rtmsStartTimestampRef,
          hideStoppedComponentTimerRef,
        });
        zoomSdk.onRTMSStatusChange(handleRTMSChange);
      } catch (error) {
        LogManager.error("Error setting up RTMS status listener:", error);
      }
    }

    return () => {
      if (hideStoppedComponentTimerRef.current) clearTimeout(hideStoppedComponentTimerRef.current);
      if (sessionStatusIntervalRef.current) clearInterval(sessionStatusIntervalRef.current);
      if (sessionStatusTimeoutRef.current) clearTimeout(sessionStatusTimeoutRef.current);
      if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current);
    };
  }, [meetingUUID, isLoading, isTranscriptOnly]);

  const start = ({ isRestartAttempt = false } = {}) =>
    onSummarize({
      summarizeState,
      setSummarizeState,
      setSummarizeClickCount,
      setRtmsRequesterName,
      setRtmsStreamId,
      setRtmsErrorType,
      setIsRtmsErrorModalOpen,
      setIsServerErrorModalOpen,
      meetingUUID,
      meetingId,
      role,
      appContext,
      isRestartAttempt,
      sessionStatusIntervalRef,
      sessionStatusTimeoutRef,
      rejectionTimeoutRef,
      rtmsStartTimestampRef,
    });

  const end = () => {
    isEndingRef.current = true;
    return onEndSummarize({
      summarizeState,
      setSummarizeState,
      setIsServerErrorModalOpen,
      meetingUUID,
      meetingId,
      role,
      appContext,
    });
  };

  // Reset the summarize/rtms state back to DEFAULT after an RTMS error is dismissed. Shared by
  // the cancel and restart paths; restart additionally flags a restart and re-runs start().
  const resetAfterRtmsError = () => {
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    setSummarizeState(SUMMARIZE_STATES.DEFAULT);
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
  };

  // Error-modal cluster: state the modals render off, plus the transitions they trigger. Bundled
  // so Home spreads it straight into <ErrorModals> instead of threading raw setters.
  const errorModals = {
    isServerErrorModalOpen,
    isRtmsErrorModalOpen,
    rtmsErrorType,
    onDismissServerError: () => setIsServerErrorModalOpen(false),
    onCancelRtmsError: () => {
      resetAfterRtmsError();
      LogManager.info("User cancelled RTMS error modal - reset to original state");
    },
    onRestartRtmsError: () => {
      resetAfterRtmsError();
      setIsRestarting(true);
      LogManager.info("User clicked restart from RTMS error modal - restarting summarization");
      start({ isRestartAttempt: true });
    },
  };

  return {
    // state read in render / passed to children
    summarizeState,
    isRestarting,
    setIsRestarting,
    isRtmsStartedByOther,
    rtmsRequesterName,
    rtmsStreamId,
    summarizeClickCount,
    isProductFeedbackModalOpen,
    setIsProductFeedbackModalOpen,
    aimsUuid,
    summaryData,
    // ready-to-spread bundle for <ErrorModals>
    errorModals,
    // setters the mount-time init hook seeds this machine's state through
    setSummarizeState,
    setIsRtmsStartedByOther,
    setRtmsRequesterName,
    setRtmsStreamId,
    // refs read during render / seeded by the init hook
    rtmsStartTimestampRef,
    isEndingRef,
    // actions
    start,
    end,
  };
};

/**
 * useStartedByOtherBanner - owns the "another host started the session" banner: it shows while
 * someone else owns the session and a requester name is known, and auto-dismisses after a minute.
 * setShowBanner is returned so a child (SessionView) can dismiss it early.
 */
export const useStartedByOtherBanner = ({ isRtmsStartedByOther, rtmsRequesterName }) => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    setShowBanner(Boolean(isRtmsStartedByOther && rtmsRequesterName));
  }, [isRtmsStartedByOther, rtmsRequesterName]);

  useEffect(() => {
    if (!showBanner) return;
    const timer = setTimeout(() => setShowBanner(false), 60000);
    return () => clearTimeout(timer);
  }, [showBanner]);

  return { showBanner, setShowBanner };
};

/**
 * useHomeNavigation - owns Home's view-navigation state machine: which top-level view is showing
 * (main / summaries / summary-detail / settings), the selected summary, and the active session
 * tab, plus the click handlers that move between them. setView / setSelectedTab are exposed for
 * the few cross-cutting transitions Home still drives (end/session-reset/settings shortcuts).
 */
export const useHomeNavigation = () => {
  const [view, setView] = useState(VIEWS.MAIN);
  const [selectedTab, setSelectedTab] = useState(SESSION_TABS.ASSISTANT);
  const [selectedSummary, setSelectedSummary] = useState(null);

  const handleSummariesClick = () => {
    if (view === VIEWS.SUMMARIES || view === VIEWS.SUMMARY_DETAIL) {
      setView(VIEWS.MAIN);
      setSelectedSummary(null);
    } else {
      setView(VIEWS.SUMMARIES);
    }
  };

  const handleSelectSummary = (summary) => {
    setSelectedSummary(summary);
    setView(VIEWS.SUMMARY_DETAIL);
  };

  const handleBackToSummaries = () => {
    setSelectedSummary(null);
    setView(VIEWS.SUMMARIES);
  };

  const handleSettingsClick = () => {
    setView(view === VIEWS.SETTINGS ? VIEWS.MAIN : VIEWS.SETTINGS);
  };

  const handleTabClick = (tabId) => () => {
    setSelectedTab(tabId);
    setView(VIEWS.MAIN);
  };

  return {
    view,
    setView,
    selectedTab,
    setSelectedTab,
    selectedSummary,
    handleSummariesClick,
    handleSelectSummary,
    handleBackToSummaries,
    handleSettingsClick,
    handleTabClick,
  };
};
